import { DistributionStage } from '../api/enumerations';
import { HiveRequest } from '../api/workOrders/types';
import { Constants } from '../constants/hiveDistribution';
import dayjs, { Dayjs } from '../lib/dayjs';

// The API writes queue timestamps as naive UTC (datetime.utcnow).
const fromApiUtc = (date: string): Dayjs => dayjs.utc(date);

/** An acceptance ban still in force (naive UTC from the API). */
export const isBanActive = (bannedUntil: string | null | undefined): boolean =>
  !!bannedUntil && dayjs.utc(bannedUntil).isAfter(dayjs());

export const isOfferingStage = (
  stage: DistributionStage | null | undefined
): boolean =>
  stage === DistributionStage.OFFERING_REGULAR ||
  stage === DistributionStage.OFFERING_OVERFLOW ||
  stage === DistributionStage.QUEUED;

/** "3h 20min", "45min" until the offer expires, or null once it expired. */
export const offerTimeLeft = (
  expiresAt: string,
  now: Dayjs = dayjs()
): string | null => {
  const minutes = fromApiUtc(expiresAt).diff(now, 'minute');
  if (minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}min`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}min`;
};

/** Absolute expiry in São Paulo time: "25/09 · 14:30". */
export const formatOfferExpiry = (expiresAt: string): string =>
  fromApiUtc(expiresAt).tz('America/Sao_Paulo').format('DD/MM [·] HH:mm');

export const formatApiUtcDate = (date: string): string =>
  fromApiUtc(date).tz('America/Sao_Paulo').format('DD/MM/YYYY');

/**
 * One-line summary of the pending offer of a work order, e.g.
 * "2ª oferta · rodada regular · expira 25/09 · 14:30 (em 3h)".
 */
export const describeOffer = (hiveRequest?: HiveRequest | null): string => {
  if (!hiveRequest?.expires_at) return '';
  const parts: string[] = [];
  if (hiveRequest.offer_round && hiveRequest.offer_stage) {
    parts.push(
      Constants.round(
        hiveRequest.offer_round,
        Constants.offerStage[hiveRequest.offer_stage]
      )
    );
  }
  const left = offerTimeLeft(hiveRequest.expires_at);
  parts.push(
    left
      ? `${Constants.expiresAt(
          formatOfferExpiry(hiveRequest.expires_at)
        )} (${left})`
      : Constants.expired
  );
  return parts.join(' · ');
};
